import type { ColumnDef } from "@tanstack/react-table";
import { LogOut } from "lucide-react";
import { Typography } from "@/components/ui/atoms/Typography";
import { Button } from "@/components/ui/atoms/Button";
import { IconWrapper } from "@/components/ui/atoms/IconWrapper";
import { dataTableSortHeader } from "@/components/ui/molecules/DataTable";
import type { SessionUser } from "@/services/admin-sessions.service";

export interface SessionTableRow {
  id: string;
  email: string;
  name: string;
  user: SessionUser;
}

export function mapSessionUserToRow(user: SessionUser): SessionTableRow {
  return {
    id: user.id,
    email: user.email ?? "",
    name: user.name ?? "",
    user,
  };
}

export interface SessionTableColumnsOptions {
  currentSub?: string;
  onDisconnect: (user: SessionUser) => void;
}

export function createSessionTableColumns({
  currentSub,
  onDisconnect,
}: SessionTableColumnsOptions): ColumnDef<SessionTableRow>[] {
  return [
    {
      accessorKey: "email",
      meta: { label: "Email" },
      header: dataTableSortHeader("Email"),
      size: 240,
      minSize: 180,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="semibold" color="primary">
          {row.original.email || "—"}
        </Typography>
      ),
    },
    {
      accessorKey: "name",
      meta: { label: "Name" },
      header: dataTableSortHeader("Name"),
      size: 180,
      minSize: 140,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="medium" color="secondary">
          {row.original.name || "—"}
        </Typography>
      ),
    },
    {
      accessorKey: "id",
      meta: { label: "Auth0 sub" },
      header: dataTableSortHeader("Auth0 sub"),
      size: 260,
      minSize: 180,
      cell: ({ row }) => (
        <Typography variant="body-xs" weight="medium" color="muted" className="font-mono">
          {row.original.id}
        </Typography>
      ),
    },
    {
      id: "actions",
      meta: { label: "Action" },
      header: () => (
        <span className="block text-right font-heading text-xs font-bold uppercase tracking-wider text-dark-invirtus">
          Action
        </span>
      ),
      size: 160,
      minSize: 140,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const isSelf = row.original.id === currentSub;
        return (
          <div
            className="flex justify-end"
            onClick={(event) => event.stopPropagation()}
          >
            {isSelf ? (
              <Typography variant="body-xs" color="muted" className="italic">
                you
              </Typography>
            ) : (
              <Button
                type="text"
                intent="danger"
                size="sm"
                label="Disconnect"
                onClick={() => onDisconnect(row.original.user)}
                icon={
                  <IconWrapper size="sm">
                    <LogOut />
                  </IconWrapper>
                }
              />
            )}
          </div>
        );
      },
    },
  ];
}
